import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { whatIsGR as whatIsGRMock } from '../mock';
import { useSiteImages } from '../context/SiteImagesContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { BallIcon, HoopIcon, RibbonIcon, RopeIcon, ClubsIcon } from '../components/GRIcons';
import { usePageMeta } from '../hooks/usePageMeta';

const WhatIsGR = () => {
  usePageMeta({ title: 'Qu\'est-ce que la GR ? - GR La Saline', description: 'Découvrez la gymnastique rythmique : ses 5 engins (ballon, cerceau, ruban, corde, massues) et ses valeurs.' });
  const images = useSiteImages();
  const config = useSiteConfig();
  const whatIsGR = {
    ...whatIsGRMock,
    ...(config?.content?.whatIsGR || {}),
    apparatus: config?.content?.whatIsGR?.apparatus?.length ? config.content.whatIsGR.apparatus : whatIsGRMock.apparatus,
  };
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const apparatusIcons = {
    ball: BallIcon,
    hoop: HoopIcon,
    ribbon: RibbonIcon,
    rope: RopeIcon,
    clubs: ClubsIcon
  };

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.whatisgr_hero}
            alt="Gymnastique Rythmique"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6"
          >
            Découverte
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6"
          >
            Qu'est-ce que la <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">GR ?</span>
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-24" ref={ref}>
        <div className="container mx-auto px-6">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto mb-24"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
              {whatIsGR.intro}
            </h2>
            <p className="text-xl text-white/70 leading-relaxed">
              {whatIsGR.description}
            </p>
          </motion.div>

          {/* Apparatus Grid */}
          <div className="mb-24">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
            >
              Les {(whatIsGR.apparatus || []).length} <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Engins</span>
            </motion.h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {whatIsGR.apparatus.map((item, index) => {
                const Icon = apparatusIcons[item.icon] || BallIcon;
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -10, scale: 1.05 }}
                    className="relative group"
                  >
                    <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/50 transition-all duration-500 text-center h-full flex flex-col items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="text-fuchsia-400" size={32} />
                      </div>
                      <h4 className="text-xl font-bold text-white mb-2">{item.name}</h4>
                      <p className="text-white/60 text-sm">{item.description}</p>
                      
                      {/* Hover Glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Image Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative h-96 rounded-2xl overflow-hidden group"
            >
              <img
                src={images.whatisgr_gallery_1}
                alt="Performance GR"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h4 className="text-2xl font-bold text-white mb-2">Élégance</h4>
                <p className="text-white/80">Chaque mouvement est une œuvre d'art</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative h-96 rounded-2xl overflow-hidden group"
            >
              <img
                src={images.whatisgr_gallery_2}
                alt="Entraînement GR"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h4 className="text-2xl font-bold text-white mb-2">Performance</h4>
                <p className="text-white/80">Sport olympique depuis 1984</p>
              </div>
            </motion.div>
          </div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Les <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Bénéfices</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
                <h4 className="text-xl font-bold text-white mb-3">Physique</h4>
                <p className="text-white/60">Développement de la souplesse, de l'équilibre et de la coordination</p>
              </div>
              <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
                <h4 className="text-xl font-bold text-white mb-3">Mental</h4>
                <p className="text-white/60">Confiance en soi, concentration et détermination</p>
              </div>
              <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
                <h4 className="text-xl font-bold text-white mb-3">Social</h4>
                <p className="text-white/60">Esprit d'équipe, solidarité et amitiés durables</p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-16"
          >
            <a
              href="/inscriptions"
              className="inline-block px-10 py-5 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full text-white font-bold text-lg hover:scale-105 transition-transform duration-300 shadow-lg shadow-fuchsia-500/50"
            >
              Essayer la GR
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhatIsGR;
